import { useMemo, useState } from 'react';
import { Box, Button, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { LineChart } from '@mui/x-charts/LineChart';
import { useQuery } from '@tanstack/react-query';

import { colors } from '@src/styles';
import {
  TAnalyticsGranularity,
  TaskAnalyticsBreakdownItemDTO,
} from '@src/services/brain/modules/v1/tasks/dto/brain-tasks.getAnalytics.response.dto';
import { StateMessage } from './StateMessage';
import { useTasksContext } from '../context/TasksContext';
import { getTasksAnalytics } from '../hooks/tasks';

const GRANULARITIES: { value: TAnalyticsGranularity; label: string }[] = [
  { value: 'day', label: 'Diário' },
  { value: 'week', label: 'Semanal' },
  { value: 'month', label: 'Mensal' },
];

const BREAKDOWN_TABS = [
  { value: 'source', label: 'Origem' },
  { value: 'type', label: 'Tipo' },
  { value: 'organization', label: 'Organização' },
] as const;

type TBreakdownTab = (typeof BREAKDOWN_TABS)[number]['value'];

const HEATMAP_METRICS = [
  { value: 'created', label: 'Criadas' },
  { value: 'finalized', label: 'Finalizadas' },
] as const;

type THeatmapMetric = (typeof HEATMAP_METRICS)[number]['value'];

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const TEXT_SECONDARY = '#9aa0a6';
const TEXT_MUTED = '#6c7075';
const SURFACE = '#242424';
const SURFACE_ALT = '#2b2b2b';

// TasksView wraps everything in selectDarkTheme, which has no dark palette, so
// x-charts renders its axis/legend text near-black on a dark surface.
const chartSX = {
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: colors.background.secondary },
  '& .MuiChartsAxis-tickLabel': { fill: colors.primary.white },
  '& .MuiChartsLegend-series text': { fill: `${colors.primary.white} !important` },
};

const card = {
  p: 2,
  borderRadius: 2,
  border: `1px solid ${colors.background.secondary}`,
  backgroundColor: SURFACE,
} as const;

const formatBucket = (bucket: string, granularity: TAnalyticsGranularity) => {
  const [year, month, day] = bucket.split('-');
  return granularity === 'month' ? `${month}/${year}` : `${day}/${month}`;
};

const formatDuration = (seconds: number | null) => {
  if (seconds === null) return '—';
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)}min`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

export const AnalyticsView = () => {
  const { filters, startDate, endDate, organizationNames, tasksSummary } = useTasksContext();
  const [granularity, setGranularity] = useState<TAnalyticsGranularity>('day');
  const [breakdownTab, setBreakdownTab] = useState<TBreakdownTab>('source');
  const [heatmapMetric, setHeatmapMetric] = useState<THeatmapMetric>('created');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'tasks-analytics',
      granularity,
      startDate?.toISOString(),
      endDate?.toISOString(),
      filters.source,
      filters.type,
      filters.organizationId,
    ],
    queryFn: () => getTasksAnalytics({ ...filters, startDate, endDate }, granularity),
  });

  const totals = (data?.byUser ?? []).reduce(
    (acc, user) => ({ concluded: acc.concluded + user.concluded, rejected: acc.rejected + user.rejected }),
    { concluded: 0, rejected: 0 },
  );
  const finalized = totals.concluded + totals.rejected;
  const rejectionRate = finalized > 0 ? (totals.rejected / finalized) * 100 : 0;
  const maxUserGroups = Math.max(...(data?.byUser ?? []).map((user) => user.groups), 1);

  const timelineLabels = useMemo(
    () => (data?.timeline ?? []).map((point) => formatBucket(point.bucket, data?.granularity ?? 'day')),
    [data],
  );

  const queueBalance = useMemo(() => {
    let running = 0;
    return (data?.timeline ?? []).map((point) => {
      running += point.created - (point.concluded + point.rejected);
      return running;
    });
  }, [data]);
  const netBalance = queueBalance.at(-1) ?? 0;
  const openNow = (tasksSummary?.pendingQuantity ?? 0) + (tasksSummary?.inProgressQuantity ?? 0);

  const breakdown = data?.breakdown[breakdownTab] ?? [];
  const maxBreakdownTotal = Math.max(...breakdown.map((item) => item.total), 1);
  const breakdownTotal = breakdown.reduce((sum, item) => sum + item.total, 0);
  const concentration = useMemo(() => {
    let accumulated = 0;
    const items = breakdown.findIndex((item) => {
      accumulated += item.total;
      return accumulated >= breakdownTotal * 0.6;
    });
    return items >= 0 ? items + 1 : 0;
  }, [breakdown, breakdownTotal]);

  const heatmap = useMemo(() => {
    const grid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    (data?.heatmap ?? []).forEach((cell) => {
      grid[cell.dow][cell.hour] = cell[heatmapMetric];
    });
    return { grid, max: Math.max(...grid.flat(), 1) };
  }, [data, heatmapMetric]);

  const breakdownLabel = (item: TaskAnalyticsBreakdownItemDTO) => {
    if (item.key === null) return 'Sem valor';
    return breakdownTab === 'organization' ? (organizationNames[item.key] ?? item.key) : item.key;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflowY: 'auto', pr: 1 }}>
        {error && !data && (
          <StateMessage
            variant="error"
            title="Não foi possível carregar a análise"
            description="A busca falhou. Verifique sua conexão e tente de novo."
            onRetry={() => refetch()}
          />
        )}
        {isLoading && !data && <AnalyticsSkeleton />}

        {data && data.byUser.length === 0 && data.timeline.every((point) => point.created === 0) && (
          <StateMessage
            variant="empty"
            title="Sem dados no período"
            description="Nenhuma tarefa foi criada ou finalizada com os filtros selecionados. Tente ampliar o período."
          />
        )}

        {data && (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <KpiTile label="Tasks finalizadas" value={finalized.toLocaleString('pt-BR')} />
              <KpiTile label="Concluídas" value={totals.concluded.toLocaleString('pt-BR')} tone="ok" />
              <KpiTile label="Rejeitadas" value={totals.rejected.toLocaleString('pt-BR')} tone="bad" />
              <KpiTile label="Taxa de rejeição" value={`${rejectionRate.toFixed(0)}%`} />
              <KpiTile label="Pessoas atuando" value={String(data.byUser.length)} />
              <KpiTile label="Em aberto" value={openNow.toLocaleString('pt-BR')} />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.35fr 1fr',
                gap: 2,
                '@media (max-width:1050px)': { gridTemplateColumns: '1fr' },
              }}
            >
              <Panel
                title="Criadas x finalizadas por período"
                action={
                  <SegmentedControl
                    options={GRANULARITIES}
                    value={granularity}
                    onChange={(value) => setGranularity(value)}
                  />
                }
              >
                {data.timeline.length === 0 ? (
                  <EmptyState />
                ) : (
                  <BarChart
                    height={280}
                    xAxis={[{ scaleType: 'band', data: timelineLabels }]}
                    series={[
                      {
                        data: data.timeline.map((point) => point.created),
                        label: 'Criadas',
                        color: colors.primary.blue,
                      },
                      {
                        data: data.timeline.map((point) => point.concluded),
                        label: 'Concluídas',
                        stack: 'finalized',
                        color: colors.primary.green,
                      },
                      {
                        data: data.timeline.map((point) => point.rejected),
                        label: 'Rejeitadas',
                        stack: 'finalized',
                        color: colors.primary.warning,
                      },
                    ]}
                    margin={{ top: 20, right: 16, bottom: 30, left: 44 }}
                  />
                )}
              </Panel>

              <Panel
                title="Atuação por pessoa"
                subtitle="Grupos atendidos (uma automação quebrada = 1 grupo, não N tasks)"
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 90px 52px 52px 52px 52px',
                    gap: 1.5,
                    pb: 0.5,
                    borderBottom: `1px solid ${colors.background.secondary}66`,
                  }}
                >
                  <ColumnLabel>Pessoa</ColumnLabel>
                  <ColumnLabel />
                  <ColumnLabel align="right">Grupos</ColumnLabel>
                  <ColumnLabel align="right">Tasks</ColumnLabel>
                  <ColumnLabel align="right">p50</ColumnLabel>
                  <ColumnLabel align="right">p90</ColumnLabel>
                </Box>
                {data.byUser.length === 0 && <EmptyState />}
                {data.byUser.map((user) => (
                  <Box
                    key={user.userId}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 90px 52px 52px 52px 52px',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      borderBottom: `1px solid ${colors.background.secondary}33`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={user.email ?? undefined}
                    >
                      {user.name ?? user.email ?? user.userId}
                    </Typography>
                    <Tooltip
                      title={`${user.groupsConcluded} grupos concluídos · ${user.groupsRejected} rejeitados`}
                      arrow
                    >
                      <Box
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: SURFACE_ALT,
                          display: 'flex',
                          gap: '2px',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(user.groupsConcluded / maxUserGroups) * 100}%`,
                            backgroundColor: colors.primary.green,
                            borderRadius: 4,
                          }}
                        />
                        <Box
                          sx={{
                            width: `${(user.groupsRejected / maxUserGroups) * 100}%`,
                            backgroundColor: colors.primary.warning,
                            borderRadius: 4,
                          }}
                        />
                      </Box>
                    </Tooltip>
                    <NumberCell>{user.groups.toLocaleString('pt-BR')}</NumberCell>
                    <NumberCell muted>{user.total.toLocaleString('pt-BR')}</NumberCell>
                    <NumberCell muted>{formatDuration(user.p50ResolutionSeconds)}</NumberCell>
                    <NumberCell muted>{formatDuration(user.p90ResolutionSeconds)}</NumberCell>
                  </Box>
                ))}
              </Panel>
            </Box>

            <Panel
              title="Fila no período"
              subtitle="Saldo acumulado de criadas menos finalizadas. Subindo, a fila cresce."
            >
              {data.timeline.length === 0 ? (
                <EmptyState />
              ) : (
                <LineChart
                  height={240}
                  xAxis={[{ scaleType: 'point', data: timelineLabels }]}
                  series={[
                    {
                      data: queueBalance,
                      label: 'Saldo acumulado',
                      color: netBalance > 0 ? colors.primary.warning : colors.primary.green,
                      showMark: false,
                      curve: 'monotoneX',
                      area: true,
                    },
                  ]}
                  margin={{ top: 20, right: 16, bottom: 30, left: 48 }}
                >
                  <ChartsReferenceLine y={0} lineStyle={{ stroke: colors.background.secondary }} />
                </LineChart>
              )}
              <Typography sx={{ fontSize: 12.5, color: colors.runs.textSecondary, mt: 1 }}>
                {netBalance === 0
                  ? 'A fila terminou o período do mesmo tamanho.'
                  : netBalance > 0
                    ? `A fila cresceu ${netBalance.toLocaleString('pt-BR')} tasks no período.`
                    : `A fila encolheu ${Math.abs(netBalance).toLocaleString('pt-BR')} tasks no período.`}
              </Typography>
            </Panel>

            <Panel
              title="De onde vêm as tasks"
              subtitle={
                concentration > 0
                  ? `${concentration} ${concentration === 1 ? 'item concentra' : 'itens concentram'} 60% do volume criado no período`
                  : 'Volume criado no período, por dimensão'
              }
              action={
                <SegmentedControl
                  options={BREAKDOWN_TABS}
                  value={breakdownTab}
                  onChange={(value) => setBreakdownTab(value)}
                />
              }
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 44px 44px 44px 58px',
                  gap: 1.5,
                  pb: 0.5,
                  borderBottom: `1px solid ${colors.background.secondary}66`,
                }}
              >
                <ColumnLabel>Item</ColumnLabel>
                <ColumnLabel align="right">Tasks</ColumnLabel>
                <ColumnLabel align="right">Grupos</ColumnLabel>
                <ColumnLabel align="right">Rej.</ColumnLabel>
                <ColumnLabel align="right">p50</ColumnLabel>
              </Box>
              {breakdown.length === 0 && <EmptyState />}
              {breakdown.map((item) => (
                <Box key={item.key ?? 'null'} sx={{ py: 0.75 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 44px 44px 44px 58px',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={breakdownLabel(item)}
                    >
                      {breakdownLabel(item)}
                    </Typography>
                    <NumberCell>{item.total.toLocaleString('pt-BR')}</NumberCell>
                    <NumberCell muted>{item.groups.toLocaleString('pt-BR')}</NumberCell>
                    <NumberCell muted>
                      {item.total > 0 ? `${Math.round((item.rejected / item.total) * 100)}%` : '—'}
                    </NumberCell>
                    <NumberCell muted>{formatDuration(item.p50ResolutionSeconds)}</NumberCell>
                  </Box>
                  <Box
                    sx={{
                      mt: 0.75,
                      height: 6,
                      borderRadius: 3,
                      width: `${(item.total / maxBreakdownTotal) * 100}%`,
                      backgroundColor: colors.primary.blue,
                    }}
                  />
                </Box>
              ))}
            </Panel>

            <Panel
              title="Quando as tasks acontecem"
              subtitle="Dia da semana × hora, no seu fuso"
              action={
                <SegmentedControl
                  options={HEATMAP_METRICS}
                  value={heatmapMetric}
                  onChange={(value) => setHeatmapMetric(value)}
                />
              }
            >
              <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '36px repeat(24, minmax(22px, 1fr))', gap: '2px' }}>
                <Box />
                {Array.from({ length: 24 }, (_, hour) => (
                  <Typography key={hour} sx={{ fontSize: 9.5, textAlign: 'center', color: TEXT_SECONDARY }}>
                    {hour % 3 === 0 ? hour : ''}
                  </Typography>
                ))}
                {heatmap.grid.map((row, dow) => (
                  <Row key={dow} label={WEEKDAYS[dow]} row={row} max={heatmap.max} />
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>0</Typography>
              <Box
                sx={{
                  height: 8,
                  width: 140,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${SURFACE_ALT}, ${colors.primary.yellow})`,
                }}
              />
              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>{heatmap.max}</Typography>
            </Box>
          </Panel>
        </>
      )}
    </Box>
  );
};

const Row = ({ label, row, max }: { label: string; row: number[]; max: number }) => (
  <>
    <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, alignSelf: 'center' }}>{label}</Typography>
    {row.map((value, hour) => (
      <Tooltip key={hour} title={`${label} ${String(hour).padStart(2, '0')}h · ${value}`} arrow>
        <Box
          sx={{
            height: 18,
            borderRadius: '3px',
            backgroundColor: value === 0 ? SURFACE_ALT : colors.primary.yellow,
            opacity: value === 0 ? 1 : 0.25 + (value / max) * 0.75,
          }}
        />
      </Tooltip>
    ))}
  </>
);

const Panel = ({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Paper elevation={0} sx={{ ...card, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
      <Box>
        <Typography sx={{ fontFamily: 'archivo', fontWeight: 700, fontSize: 14, color: colors.primary.white }}>
          {title}
        </Typography>
        {subtitle && <Typography sx={{ fontSize: 11.5, color: TEXT_SECONDARY, mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
);

const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) => (
  <Box sx={{ display: 'flex', border: `1px solid ${colors.background.secondary}`, borderRadius: 2, p: '3px' }}>
    {options.map((option) => (
      <Button
        key={option.value}
        onClick={() => onChange(option.value)}
        sx={{
          minWidth: 0,
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          color: value === option.value ? colors.primary.white : TEXT_SECONDARY,
          backgroundColor: value === option.value ? SURFACE_ALT : 'transparent',
        }}
      >
        {option.label}
      </Button>
    ))}
  </Box>
);

const AnalyticsSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} variant="rounded" height={92} sx={{ minWidth: 150, flex: '1 1 150px' }} />
      ))}
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1.35fr 1fr',
        gap: 2,
        '@media (max-width:1050px)': { gridTemplateColumns: '1fr' },
      }}
    >
      <Skeleton variant="rounded" height={340} />
      <Skeleton variant="rounded" height={340} />
    </Box>
    <Skeleton variant="rounded" height={280} />
  </Box>
);

const EmptyState = () => (
  <Typography sx={{ color: TEXT_SECONDARY, fontSize: 13, py: 1 }}>Sem dados no período.</Typography>
);

const ColumnLabel = ({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) => (
  <Typography
    sx={{
      fontSize: 10,
      letterSpacing: '.5px',
      textTransform: 'uppercase',
      color: TEXT_MUTED,
      textAlign: align,
    }}
  >
    {children}
  </Typography>
);

const NumberCell = ({ children, muted }: { children: React.ReactNode; muted?: boolean }) => (
  <Typography
    sx={{
      fontFamily: 'monospace',
      fontSize: 12,
      textAlign: 'right',
      color: muted ? TEXT_SECONDARY : colors.primary.white,
    }}
  >
    {children}
  </Typography>
);

const TONE: Record<string, string> = { ok: colors.primary.green, bad: colors.primary.warning };

const KpiTile = ({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'bad' }) => (
  <Paper elevation={0} sx={{ ...card, minWidth: 150, flex: '1 1 150px' }}>
    <Typography
      sx={{
        fontFamily: 'archivo',
        fontSize: 11,
        letterSpacing: '.6px',
        textTransform: 'uppercase',
        color: TEXT_SECONDARY,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'archivo',
        fontSize: 30,
        fontWeight: 700,
        mt: 0.5,
        color: tone ? TONE[tone] : colors.primary.white,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Paper>
);
